import React, { useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Button, Row, Col, Typography, message } from 'antd';
import type { InvoiceListItem } from '../../store/api/financeApi';
import { useAdjustClientInvoiceMutation } from '../../store/api/financeApi';

const { Text } = Typography;

export interface CreateInvoiceAdjustModalProps {
  open: boolean;
  sourceInvoice: InvoiceListItem | null;
  onCancel: () => void;
  onSuccess?: () => void;
}

const CreateInvoiceAdjustModal: React.FC<CreateInvoiceAdjustModalProps> = ({
  open,
  sourceInvoice,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [adjust, { isLoading }] = useAdjustClientInvoiceMutation();

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ amountWithVAT: undefined, reason: '' });
    }
  }, [open, form]);

  const handleOk = async () => {
    if (!sourceInvoice) return;
    const v = await form.validateFields();
    try {
      const res = await adjust({
        invoiceId: sourceInvoice.id,
        amountWithVAT: Number(v.amountWithVAT),
        reason: v.reason ? String(v.reason).trim() : undefined
      }).unwrap();
      message.success(
        `Корректировка создана. Итого по дебиторке (сумма обязательств): ${res.totals.netAmountWithVAT.toLocaleString('ru-RU')} ₽`
      );
      form.resetFields();
      onSuccess?.();
      onCancel();
    } catch (e: any) {
      message.error(e?.data?.error || e?.data?.code || 'Не удалось создать корректировку');
    }
  };

  return (
    <Modal
      title="Корректировка счёта"
      open={open}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      footer={null}
      destroyOnClose
      width={480}
    >
      {sourceInvoice && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Базовый счёт: <strong>№{sourceInvoice.number}</strong> (id {sourceInvoice.id}), сумма{' '}
          {Number(sourceInvoice.amountWithVAT ?? 0).toLocaleString('ru-RU')} ₽
        </Text>
      )}
      <Form form={form} layout="vertical" onFinish={() => void handleOk()}>
        <Form.Item
          name="amountWithVAT"
          label="Сумма корректировки с НДС (+ увеличение, − уменьшение)"
          rules={[
            { required: true, message: 'Укажите сумму' },
            {
              validator: (_, val) =>
                val != null && Number(val) !== 0 ? Promise.resolve() : Promise.reject(new Error('Сумма не должна быть нулём'))
            }
          ]}
        >
          <InputNumber style={{ width: '100%' }} step={100} placeholder="Например: -20000" />
        </Form.Item>
        <Form.Item name="reason" label="Причина">
          <Input.TextArea rows={3} placeholder="Снижение стоимости перевозки и т.п." maxLength={500} showCount />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Button block onClick={onCancel}>
              Отмена
            </Button>
          </Col>
          <Col span={12}>
            <Button type="primary" block htmlType="submit" loading={isLoading}>
              Создать корректировку
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CreateInvoiceAdjustModal;
